import firebase from 'firebase/app';
// Goals Store Module
// Simple team goals management - stores goals as text on team records

interface GoalsState {
  editingTeamId: string | null;
  selectedTeam: string;
  searchKeyword: string;
  goalsTeamAI: any;
  showArchivedGoals: boolean;
}

const state: GoalsState = {
  editingTeamId: null,
  selectedTeam: 'All Teams',
  searchKeyword: '',
  goalsTeamAI: {},
  showArchivedGoals: false,
};

const mutations = {
  setEditingTeamId(state: GoalsState, teamId: string | null) {
    state.editingTeamId = teamId;
  },
  setSelectedTeam(state: GoalsState, teamId: string) {
    state.selectedTeam = teamId;
  },
  setSearchKeyword(state: GoalsState, keyword: string) {
    state.searchKeyword = keyword;
  },
  setGoalsTeamAI(state: GoalsState, goalsTeamAI: any) {
    // Use Object.assign to ensure Vue reactivity detects the change
    state.goalsTeamAI = Object.assign({}, goalsTeamAI);
  },
  setShowArchivedGoals(state: GoalsState, show: boolean) {
    state.showArchivedGoals = show;
  },
};

const actions = {
  openEditModal({ commit }: any, teamId: string) {
    commit('setEditingTeamId', teamId);
  },

  closeEditModal({ commit }: any) {
    commit('setEditingTeamId', null);
  },

  async getGoalsTeamAI({ commit, rootGetters, dispatch ,rootState}: any) {
    const goals_team_ai: Record<string, any[]> = {};
    await firebase.firestore().collection('goals_team_ai').where('company_id', '==', rootState.authen.user.company).get().then((snapshot)=>{
      snapshot.docs.forEach((doc)=>{
        const data = doc.data();
        const docId = doc.id;

        if(!goals_team_ai[data.team_id]){
          goals_team_ai[data.team_id] = [];
        }

        if(data.goals && Array.isArray(data.goals)){
          // New format: document contains a goals array
          data.goals.forEach((goal: any, index: number) => {
            // Ensure each goal has a goal_id (use index if not present)
            goals_team_ai[data.team_id].push({
              goal_id: goal.goal_id_ai || `${docId}_${index}`,
              quick_preview: goal.quick_preview || '',
              goal_description: goal.goal_description || goal.quick_preview || '',
              archived: goal.archived || false,
              archivedAt: goal.archivedAt || null,
              parent_goal_ids: goal.parent_goal_ids || [],
              teamId: data.team_id, // Store team reference for lookups
            });
          });
        } else if (data.goal_id || data.quick_preview) {
          // Old format: document itself is a goal
          goals_team_ai[data.team_id].push({
            goal_id: data.goal_id_ai || docId,
            quick_preview: data.quick_preview || '',
            goal_description: data.goal_description || data.quick_preview || '',
            archived: data.archived || false,
            archivedAt: data.archivedAt || null,
            parent_goal_ids: data.parent_goal_ids || [],
            teamId: data.team_id, // Store team reference for lookups
          });
        }
      });
    });
    // Create a new object reference to ensure Vue reactivity
    commit('setGoalsTeamAI', { ...goals_team_ai });
  },

  async updateSingleGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId, quick_preview, goal_description }: { teamId: string; goalId: string; quick_preview?: string; goal_description?: string }
  ) {
    // Save old state for potential rollback
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      // 1. Optimistic local update
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          if (quick_preview !== undefined) {
            updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], quick_preview };
          }
          if (goal_description !== undefined) {
            updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], goal_description };
          }
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      // 2. Firestore update
      const companyId = rootState.authen.user.company;
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      if (snapshot.empty) {
        // Rollback on error
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('No goals document found for team:', teamId);
        return { success: false, error: 'Goals document not found' };
      }

      // Find the document containing this goal
      let targetDoc: any = null;
      let goalIndex = -1;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.goals && Array.isArray(data.goals)) {
          const idx = data.goals.findIndex((g: any) => g.goal_id_ai === goalId);
          if (idx !== -1) {
            targetDoc = doc;
            goalIndex = idx;
            break;
          }
        }
      }

      if (!targetDoc || goalIndex === -1) {
        // Rollback on error
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return { success: false, error: 'Goal not found' };
      }

      // Get current goals array and update the specific goal
      const docData = targetDoc.data();
      const updatedGoals = [...docData.goals];

      if (quick_preview !== undefined) {
        updatedGoals[goalIndex].quick_preview = quick_preview;
      }
      if (goal_description !== undefined) {
        updatedGoals[goalIndex].goal_description = goal_description;
      }

      // Update the document in Firestore
      await targetDoc.ref.update({
        goals: updatedGoals
      });

      // No need to refresh - optimistic update already applied
      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);
      await dispatch('getGoalsTeamAI');
      console.error('Error updating single goal:', error);
      return { success: false, error };
    }
  },

  async saveTeamGoals({ commit, rootGetters, dispatch }: any, { teamId, goals }: { teamId: string; goals: string }) {
    // TODO: Backend - AI will parse and structure goals into individual items with labels and descriptions
    // For now, save raw text to team.goals field
    try {
      // Get teams from team store and update
      const teams = rootGetters['team/teamsall'] || [];
      const team = teams.find((t: any) => t.key === teamId);

      if (team) {
        team.goals = goals;
      }

      firebase.database().ref(`team/${teamId}`).update({
        goals: goals
      })

      // TODO: Call API to persist to backend
      // const response = await updateTeamGoals(teamId, goals);
      await dispatch('main/addAndUpdateGoalTeam',{teamId:teamId, goals:goals}, {root:true});
      await dispatch('getGoalsTeamAI');
      commit('setEditingTeamId', null);

      return { success: true };
    } catch (error) {
      console.error('Error saving team goals:', error);
      return { success: false, error };
    }
  },

  /**
   * Save structured goals directly without AI transformation
   * Used by the "Advanced" tab in the goals modal
   * Handles both updating existing goals and adding new ones
   */
  async saveStructuredGoals(
    { commit, rootState, dispatch }: any,
    { teamId, goals }: { teamId: string; goals: Array<{ goal_id?: string; title: string; description: string; parent_goal_ids?: string[] }> }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      
      // Filter out empty goals (no title)
      const validGoals = goals.filter(g => g.title && g.title.trim() !== '');
      
      if (validGoals.length === 0) {
        return { success: false, error: 'No valid goals to save' };
      }

      // Check if document already exists for this team
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      // Separate existing goals (to update) from new goals (to add)
      const goalsToUpdate = validGoals.filter(g => g.goal_id);
      const goalsToAdd = validGoals.filter(g => !g.goal_id);

      // Generate structured goals for new entries
      const newStructuredGoals = goalsToAdd.map((goal) => ({
        goal_id_ai: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        quick_preview: goal.title.trim(),
        goal_description: (goal.description && goal.description.trim()) || goal.title.trim(),
        parent_goal_ids: goal.parent_goal_ids || [],
        archived: false,
        archivedAt: null,
        createdAt: new Date().toISOString(),
        source: 'manual', // Indicate this was manually entered, not AI-generated
      }));

      let updatedCount = 0;
      let addedCount = newStructuredGoals.length;

      if (snapshot.empty) {
        // Create new document with all goals using compound ID format
        const compoundDocId = `${companyId}--${teamId}`;
        const goalTeamId = firebase.firestore().collection('goals_team_ai').doc().id;
        await firebase.firestore().collection('goals_team_ai').doc(compoundDocId).set({
          company_id: companyId,
          team_id: teamId,
          goal_team_id: goalTeamId,
          goals: newStructuredGoals,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } else {
        // Update existing document
        const doc = snapshot.docs[0];
        const existingData = doc.data();
        const existingGoals: any[] = existingData.goals || [];
        
        // Update existing goals in place
        const updatedGoals = existingGoals.map((existingGoal: any) => {
          const updateData = goalsToUpdate.find(g => g.goal_id === existingGoal.goal_id_ai);
          if (updateData) {
            updatedCount++;
            return {
              ...existingGoal,
              quick_preview: updateData.title.trim(),
              goal_description: (updateData.description && updateData.description.trim()) || updateData.title.trim(),
              parent_goal_ids: updateData.parent_goal_ids || existingGoal.parent_goal_ids || [],
              updatedAt: new Date().toISOString(),
            };
          }
          return existingGoal;
        });
        
        // Add new goals
        const finalGoals = [...updatedGoals, ...newStructuredGoals];
        
        await doc.ref.update({
          goals: finalGoals,
          updatedAt: new Date().toISOString(),
        });
      }

      // Refresh local state
      await dispatch('getGoalsTeamAI');
      commit('setEditingTeamId', null);

      return {
        success: true,
        goalsAdded: addedCount,
        goalsUpdated: updatedCount,
        newGoals: newStructuredGoals, // Return newly created goals with their IDs
        message: `${updatedCount > 0 ? `${updatedCount} updated` : ''}${updatedCount > 0 && addedCount > 0 ? ', ' : ''}${addedCount > 0 ? `${addedCount} added` : ''}`
      };
    } catch (error) {
      console.error('Error saving structured goals:', error);
      return { success: false, error };
    }
  },

  /**
   * Archive a goal (soft-delete)
   * Sets archived: true and archivedAt timestamp
   */
  async archiveGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    // Save old state for potential rollback
    const oldGoalsTeamAI = { ...state.goalsTeamAI };
    const archivedAt = new Date().toISOString();

    try {
      // 1. Optimistic local update
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          updatedTeamGoals[goalIndex] = { ...updatedTeamGoals[goalIndex], archived: true, archivedAt };
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      // 2. Firestore update
      const companyId = rootState.authen.user.company;
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      if (snapshot.empty) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('No goals document found for team:', teamId);
        return { success: false, error: 'Goals document not found' };
      }

      // Find the document containing this goal
      let targetDoc: any = null;
      let goalIndex = -1;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.goals && Array.isArray(data.goals)) {
          const idx = data.goals.findIndex((g: any) => g.goal_id_ai === goalId);
          if (idx !== -1) {
            targetDoc = doc;
            goalIndex = idx;
            break;
          }
        }
      }

      if (!targetDoc || goalIndex === -1) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return { success: false, error: 'Goal not found' };
      }

      // Get current goals array and update the specific goal
      const docData = targetDoc.data();
      const updatedGoals = [...docData.goals];
      updatedGoals[goalIndex].archived = true;
      updatedGoals[goalIndex].archivedAt = archivedAt;

      // Update the document in Firestore
      await targetDoc.ref.update({
        goals: updatedGoals
      });

      // No need to refresh - optimistic update already applied
      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);
      await dispatch('getGoalsTeamAI');
      console.error('Error archiving goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Unarchive a goal (restore from archive)
   * Sets archived: false
   */
  async unarchiveGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    // Save old state for potential rollback
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      // 1. Optimistic local update
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const goalIndex = teamGoals.findIndex((g: any) => g.goal_id === goalId);
        if (goalIndex !== -1) {
          const updatedTeamGoals = [...teamGoals];
          const { archivedAt, ...goalWithoutArchivedAt } = updatedTeamGoals[goalIndex];
          updatedTeamGoals[goalIndex] = { ...goalWithoutArchivedAt, archived: false };
          commit('setGoalsTeamAI', { ...state.goalsTeamAI, [teamId]: updatedTeamGoals });
        }
      }

      // 2. Firestore update
      const companyId = rootState.authen.user.company;
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      if (snapshot.empty) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('No goals document found for team:', teamId);
        return { success: false, error: 'Goals document not found' };
      }

      // Find the document containing this goal
      let targetDoc: any = null;
      let goalIndex = -1;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.goals && Array.isArray(data.goals)) {
          const idx = data.goals.findIndex((g: any) => g.goal_id_ai === goalId);
          if (idx !== -1) {
            targetDoc = doc;
            goalIndex = idx;
            break;
          }
        }
      }

      if (!targetDoc || goalIndex === -1) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return { success: false, error: 'Goal not found' };
      }

      // Get current goals array and update the specific goal
      const docData = targetDoc.data();
      const updatedGoals = [...docData.goals];
      updatedGoals[goalIndex].archived = false;
      delete updatedGoals[goalIndex].archivedAt;

      // Update the document in Firestore
      await targetDoc.ref.update({
        goals: updatedGoals
      });

      // No need to refresh - optimistic update already applied
      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);
      await dispatch('getGoalsTeamAI');
      console.error('Error unarchiving goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Permanently delete a goal
   * Removes the goal from the goals array
   */
  async deleteGoal(
    { commit, state, rootState, dispatch }: any,
    { teamId, goalId }: { teamId: string; goalId: string }
  ) {
    // Save old state for potential rollback
    const oldGoalsTeamAI = { ...state.goalsTeamAI };

    try {
      // 1. Optimistic local update — build a fully new object for Vue reactivity
      const teamGoals = state.goalsTeamAI[teamId];
      if (teamGoals && Array.isArray(teamGoals)) {
        const updatedTeamGoals = teamGoals.filter((g: any) => g.goal_id !== goalId);
        const newGoalsTeamAI: Record<string, any[]> = {};
        Object.keys(state.goalsTeamAI).forEach((key) => {
          if (key === teamId) {
            if (updatedTeamGoals.length > 0) {
              newGoalsTeamAI[key] = updatedTeamGoals;
            }
            // If empty, don't include the key — team has no goals
          } else {
            newGoalsTeamAI[key] = state.goalsTeamAI[key];
          }
        });
        commit('setGoalsTeamAI', newGoalsTeamAI);
      }

      // 2. Firestore update
      const companyId = rootState.authen.user.company;
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      if (snapshot.empty) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('No goals document found for team:', teamId);
        return { success: false, error: 'Goals document not found' };
      }

      // Find the document containing this goal
      let targetDoc: any = null;
      let goalIndex = -1;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.goals && Array.isArray(data.goals)) {
          // Match by goal_id_ai first, then fallback to index-based ID format (docId_index)
          let idx = data.goals.findIndex((g: any) => g.goal_id_ai === goalId);
          if (idx === -1) {
            // Handle fallback IDs generated during fetch: "${docId}_${index}"
            const fallbackMatch = goalId.match(/^(.+)_(\d+)$/);
            if (fallbackMatch && fallbackMatch[1] === doc.id) {
              idx = parseInt(fallbackMatch[2], 10);
              if (idx < 0 || idx >= data.goals.length) {
                idx = -1;
              }
            }
          }
          if (idx !== -1) {
            targetDoc = doc;
            goalIndex = idx;
            break;
          }
        }
      }

      if (!targetDoc || goalIndex === -1) {
        commit('setGoalsTeamAI', oldGoalsTeamAI);
        console.error('Goal not found:', goalId);
        return { success: false, error: 'Goal not found' };
      }

      // Get current goals array and remove the specific goal
      const docData = targetDoc.data();
      const updatedGoals = [...docData.goals];
      updatedGoals.splice(goalIndex, 1);

      // Update the document in Firestore
      await targetDoc.ref.update({
        goals: updatedGoals
      });

      // If all goals for this team are now deleted, clear the legacy team.goals string in RTDB
      const remainingActive = updatedGoals.filter((g: any) => !g.archived);
      if (remainingActive.length === 0) {
        firebase.database().ref(`team/${teamId}`).update({ goals: '' });
      }

      // Background refresh to ensure consistency with Firestore
      dispatch('getGoalsTeamAI');
      return { success: true };
    } catch (error) {
      // Rollback on error and refresh from server
      commit('setGoalsTeamAI', oldGoalsTeamAI);
      await dispatch('getGoalsTeamAI');
      console.error('Error deleting goal:', error);
      return { success: false, error };
    }
  },

  /**
   * Get ancestor teams for a given team (traverses up the parent chain)
   * Returns array of teams from direct parent to root
   */
  getAncestorTeams({ rootGetters }: any, teamId: string): any[] {
    const allTeams = rootGetters['team/teamsall'] || [];
    const ancestors: any[] = [];
    
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    return ancestors;
  },

  /**
   * Get all goals from ancestor teams for linking purposes
   * Returns goals grouped by team with team metadata
   */
  getAncestorTeamGoals({ state, dispatch, rootGetters }: any, teamId: string): any[] {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Get ancestor teams
    const ancestors: any[] = [];
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    // For each ancestor team, get their goals
    ancestors.forEach((team: any) => {
      const teamGoals = goalsTeamAI[team.key] || [];
      // Only include non-archived goals for linking
      const activeGoals = teamGoals.filter((g: any) => !g.archived);
      
      if (activeGoals.length > 0) {
        result.push({
          team: {
            key: team.key,
            name: team.name,
            color: team.color || '#747474',
          },
          goals: activeGoals.map((goal: any) => ({
            goal_id: goal.goal_id,
            quick_preview: goal.quick_preview,
            goal_description: goal.goal_description,
            teamId: team.key,
            teamName: team.name,
            teamColor: team.color || '#747474',
          })),
        });
      }
    });
    
    return result;
  },

  /**
   * Update parent goal links for a goal
   * Sets the parent_goal_ids array for the specified goal
   */
  async updateGoalParentLinks(
    { rootState, dispatch }: any,
    { teamId, goalId, parentGoalIds }: { teamId: string; goalId: string; parentGoalIds: string[] }
  ) {
    try {
      const companyId = rootState.authen.user.company;
      
      // Find the document that contains this goal
      const snapshot = await firebase.firestore()
        .collection('goals_team_ai')
        .where('company_id', '==', companyId)
        .where('team_id', '==', teamId)
        .get();

      if (snapshot.empty) {
        console.error('No goals document found for team:', teamId);
        return { success: false, error: 'Goals document not found' };
      }

      // Find the document containing this goal
      let targetDoc: any = null;
      let goalIndex = -1;

      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.goals && Array.isArray(data.goals)) {
          const idx = data.goals.findIndex((g: any) => g.goal_id_ai === goalId);
          if (idx !== -1) {
            targetDoc = doc;
            goalIndex = idx;
            break;
          }
        }
      }

      if (!targetDoc || goalIndex === -1) {
        console.error('Goal not found:', goalId);
        return { success: false, error: 'Goal not found' };
      }

      // Get current goals array and update the parent_goal_ids
      const docData = targetDoc.data();
      const updatedGoals = [...docData.goals];
      updatedGoals[goalIndex].parent_goal_ids = parentGoalIds;

      // Update the document in Firestore
      await targetDoc.ref.update({
        goals: updatedGoals
      });

      // Refresh local state
      await dispatch('getGoalsTeamAI');

      return { success: true };
    } catch (error) {
      console.error('Error updating goal parent links:', error);
      return { success: false, error };
    }
  },
};

const getters = {
  teams: (state: any, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['team/teamsall'] || [];
  },
  selectedTeam: (state: GoalsState) => state.selectedTeam,
  searchKeyword: (state: GoalsState) => state.searchKeyword,
  filteredTeams: (state: GoalsState, getters: any) => {
    let teams = getters.teams || [];

    // Filter by search keyword
    if (state.searchKeyword) {
      const keyword = state.searchKeyword.toLowerCase();
      teams = teams.filter((team: any) =>
        team.name && team.name.toLowerCase().includes(keyword)
      );
    }

    // Filter by selected team
    if (state.selectedTeam && state.selectedTeam !== 'All Teams') {
      // Show selected team and all its descendants
      const collectTeamAndDescendants = (teamId: string, allTeams: any[]): any[] => {
        const result: any[] = [];
        const team: any = allTeams.find((t: any) => t.key === teamId);
        if (team) result.push(team);

        // Find children
        const children = allTeams.filter((t: any) => t.parent === teamId);
        children.forEach((child: any) => {
          result.push(...collectTeamAndDescendants(child.key, allTeams));
        });

        return result;
      };

      teams = collectTeamAndDescendants(state.selectedTeam, getters.teams);
    }

    return teams;
  },
  editingTeamId: (state: GoalsState) => state.editingTeamId,
  editingTeam: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    if (!state.editingTeamId) return null;
    const teams = rootGetters['team/teamsall'] || [];
    return teams.find((t: any) => t.key === state.editingTeamId);
  },
  goalsTeamAI: (state: GoalsState) => state.goalsTeamAI,
  showArchivedGoals: (state: GoalsState) => state.showArchivedGoals,

  // Active goals (non-archived) by team
  activeGoalsTeamAI: (state: GoalsState) => {
    const result: Record<string, any[]> = {};
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      result[teamId] = state.goalsTeamAI[teamId].filter((goal: any) => !goal.archived);
    });
    return result;
  },

  // Archived goals by team
  archivedGoalsTeamAI: (state: GoalsState) => {
    const result: Record<string, any[]> = {};
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      const archivedGoals = state.goalsTeamAI[teamId].filter((goal: any) => goal.archived);
      if (archivedGoals.length > 0) {
        result[teamId] = archivedGoals;
      }
    });
    return result;
  },

  // Count of archived goals across all teams
  archivedGoalsCount: (state: GoalsState) => {
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      count += state.goalsTeamAI[teamId].filter((goal: any) => goal.archived).length;
    });
    return count;
  },

  // Goal alignment getters - consume focus store data
  goalAlignmentStats: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/goalAlignmentStats'] || {};
  },

  alignedFocusCount: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/alignedFocusCount'] || 0;
  },

  unalignedFocusCount: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    return rootGetters['focus/unalignedFocusCount'] || 0;
  },

  // Enrich each goal with focus stats
  goalsWithFocusStats: (state: GoalsState, getters: any) => {
    const alignmentStats = getters.goalAlignmentStats;
    const goalsTeamAI = state.goalsTeamAI;
    const enrichedGoals: Record<string, any[]> = {};

    Object.keys(goalsTeamAI).forEach((teamId) => {
      enrichedGoals[teamId] = goalsTeamAI[teamId].map((goal: any) => {
        const stats = alignmentStats[goal.goal_id] || {
          peopleCount: 0,
          openCount: 0,
          doneCount: 0,
          totalItems: 0,
        };
        return {
          ...goal,
          focusStats: stats,
          hasFocus: stats.totalItems > 0,
        };
      });
    });

    return enrichedGoals;
  },

  // List of goals with no focus (gaps)
  goalsWithoutFocus: (state: GoalsState, getters: any) => {
    const goalsWithStats = getters.goalsWithFocusStats;
    const gaps: any[] = [];

    Object.keys(goalsWithStats).forEach((teamId) => {
      goalsWithStats[teamId].forEach((goal: any) => {
        if (!goal.hasFocus) {
          gaps.push({
            ...goal,
            teamId,
          });
        }
      });
    });

    return gaps;
  },

  // Total goals count across all teams
  totalGoalsCount: (state: GoalsState) => {
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      count += state.goalsTeamAI[teamId].length;
    });
    return count;
  },

  // Count of goals that have at least one focus item
  goalsWithFocusCount: (state: GoalsState, getters: any) => {
    const alignmentStats = getters.goalAlignmentStats;
    let count = 0;
    Object.keys(state.goalsTeamAI).forEach((teamId) => {
      state.goalsTeamAI[teamId].forEach((goal: any) => {
        if (alignmentStats[goal.goal_id] && alignmentStats[goal.goal_id].totalItems > 0) {
          count++;
        }
      });
    });
    return count;
  },

  /**
   * Get goals from ancestor teams for a given team (for linking modal)
   * Returns goals grouped by team with team metadata
   */
  ancestorGoalsForTeam: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (teamId: string) => {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Get ancestor teams by traversing parent chain
    const ancestors: any[] = [];
    let currentTeam = allTeams.find((t: any) => t.key === teamId);
    
    while (currentTeam && currentTeam.parent) {
      const parentTeam = allTeams.find((t: any) => t.key === currentTeam.parent);
      if (parentTeam) {
        ancestors.push(parentTeam);
        currentTeam = parentTeam;
      } else {
        break;
      }
    }
    
    // For each ancestor team, get their active goals
    ancestors.forEach((team: any) => {
      const teamGoals = goalsTeamAI[team.key] || [];
      // Only include non-archived goals for linking
      const activeGoals = teamGoals.filter((g: any) => !g.archived);
      
      if (activeGoals.length > 0) {
        result.push({
          team: {
            key: team.key,
            name: team.name,
            color: team.color || '#747474',
          },
          goals: activeGoals.map((goal: any) => ({
            goal_id: goal.goal_id,
            quick_preview: goal.quick_preview,
            goal_description: goal.goal_description,
            teamId: team.key,
            teamName: team.name,
            teamColor: team.color || '#747474',
          })),
        });
      }
    });
    
    return result;
  },

  /**
   * Get full details for a list of goal IDs (for tooltip display)
   * Returns array of goal objects with team info
   */
  getParentGoalDetails: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => (goalIds: string[]) => {
    if (!goalIds || goalIds.length === 0) return [];
    
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    // Create a map of all goals for fast lookup
    const goalMap: Record<string, any> = {};
    Object.keys(goalsTeamAI).forEach((teamId) => {
      const team = allTeams.find((t: any) => t.key === teamId);
      goalsTeamAI[teamId].forEach((goal: any) => {
        goalMap[goal.goal_id] = {
          ...goal,
          teamId,
          teamName: (team && team.name) ? team.name : 'Unknown Team',
          teamColor: (team && team.color) ? team.color : '#747474',
        };
      });
    });
    
    // Lookup each requested goal ID
    goalIds.forEach((goalId: string) => {
      const goal = goalMap[goalId];
      if (goal) {
        result.push(goal);
      }
    });
    
    return result;
  },

  /**
   * Flat list of all goals across all teams (for reverse lookups)
   */
  allGoalsFlat: (state: GoalsState, getters: any, rootState: any, rootGetters: any) => {
    const allTeams = rootGetters['team/teamsall'] || [];
    const goalsTeamAI = state.goalsTeamAI;
    const result: any[] = [];
    
    Object.keys(goalsTeamAI).forEach((teamId) => {
      const team = allTeams.find((t: any) => t.key === teamId);
      goalsTeamAI[teamId].forEach((goal: any) => {
        result.push({
          ...goal,
          teamId,
          teamName: (team && team.name) ? team.name : 'Unknown Team',
          teamColor: (team && team.color) ? team.color : '#747474',
        });
      });
    });
    
    return result;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
